<template>
  <div
    class="quiz-result-container"
    :class="[
      extendClasses,
      {
        'animate__animated animate__slideInUp animate__faster': !isShowPopupStreak,
        'invisible': isShowPopupStreak,
        'is-audience-paced': presentation.audiencePacing,
      }
    ]"
  >
    <div class="quiz-result-info">
      <div class="quiz-result-marker">
        <img
          v-if="isResultTimeout"
          class="quiz-result-symbol"
          :src="require('@/assets/images/visualize-items/times-up.png')"
          width="62"
          height="64"
          alt=""
        >
        <img
          v-else-if="result.correct"
          class="quiz-result-symbol"
          :src="require('@/assets/images/visualize-items/correct.png')"
          alt=""
          width="63"
          height="67"
        >
        <img
          v-else-if="!result.correct"
          class="quiz-result-symbol"
          :src="require('@/assets/images/visualize-items/incorrect.png')"
          alt=""
          width="63"
          height="67"
        >
        <span
          v-if="result.correct && !teamPlay"
          class="quiz-result-number"
        >
          {{ result.streak }}
        </span>
      </div>
      <div class="quiz-result-text">
        {{ quizResultText }}
      </div>
      <div
        v-if="!isResultTimeout"
        class="quiz-result-point"
        :class="{
          'bonus': isStreakBonusAdded,
        }"
      >
        <span
          :class="{
            'animate__animated animate__bigPulse': showFinalAnimationEffect
          }"
        >
          <b>{{ `+ ${resultPoint}` }}</b>
        </span>
        <transition
          enter-active-class="animate__animated animate__bounceIn"
          leave-active-class="animate__animated animate__fadeOutDown"
        >
          <div
            v-if="isActiveStreakBonus && result.streak >= 2 && isShowBonusBadge"
            class="streak-bonus"
            :class="[`tier-${bonusTier}`]"
          >
            <span>{{ `+${result.streakBonusPoint}` }}</span>
          </div>
        </transition>
      </div>
    </div>
    <div class="quiz-result-desc">
      {{ quizResultDesc }}
    </div>
    <QuizTeamResultTextV2
      v-if="teamPlay && teamQuizResult && teamQuizResult.loaded"
      :teamScoringRule="teamScoringRule"
      :resultSolo="result"
      :resultTeam="teamQuizResult"
    />

    <PopupStreak
      v-if="isShowPopupStreak"
      :streak="result.streak"
      :point="point"
      :streakBonus="result.streakBonusPoint"
      :isActiveStreakBonus="isActiveStreakBonus"
      :isShowBonusBadge="isShowBonusBadge"
      :isStreakBonusAdded="isStreakBonusAdded"
      :bonusTier="bonusTier"
      :showFinalAnimationEffect="showFinalAnimationEffect"
      :quizResultText="quizResultText"
      :quizResultDesc="quizResultDesc"
      :streakNote="streakNote"
      @showPopup="handlePopupStreak"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import StreakAceMixin from '@/mixins/StreakAceMixin'
import QuizTeamResultTextV2 from './QuizTeamResultTextV2.vue'
import PopupStreak from './popups/PopupStreak.vue'

const ANIMATE_DURATION = 1000
const FPS = 1000 / 60
const MAX_BONUS_TIER = 5

export default {
  components: {
    QuizTeamResultTextV2,
    PopupStreak,
  },
  mixins: [StreakAceMixin],
  props: {
    // Response from /api/create-answer or /api/answer/quiz-result
    result: {
      type: Object,
      required: true,
    },
    teamQuizResult: {
      type: Object,
      default: () => {},
    },
    teamPlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      point: 0,
      updatePointInterval: null,
      visiblePopupStreak: false,
      isShowBonusBadge: false,
      showFinalAnimationEffect: false,
    }
  },
  computed: {
    ...mapState({
      slide: (state) => state.slide,
      presentation: (state) => state.presentation,
      teamScoringRule: (state) => state.presentation.teamScoringRule,
    }),
    isResultTimeout() {
      return this.result && !this.result.correct && this.result.timeout
    },
    extendClasses() {
      if (this.isResultTimeout) {
        return 'times-up'
      }

      if (this.result && this.result.correct) {
        return 'correct'
      }

      if (this.result && !this.result.correct) {
        return 'incorrect'
      }

      return null
    },
    quizResultText() {
      if (this.isResultTimeout) {
        return this.$t('timesUp')
      }

      if (this.teamPlay) {
        return this.result.correct
          ? this.$t('Correct')
          : this.$t('Incorrect')
      }

      // solo
      if (this.result.correct) {
        return this.$t(this.streakNote)
      }

      return this.isLosingStreak(this.result.streak, this.result.lastStreak)
        ? this.$t(this.streakNote)
        : this.$t('Incorrect')
    },
    quizResultDesc() {
      if (this.result.correct && this.result.typeCorrectAnswer === 3) {
        return this.$t('partiallyCorrect')
      }

      return ''
    },
    resultPoint() {
      return this.point ? this.point : 0
    },
    isManualRevealAndNotShowCorrectOptionYet() {
      return this.presentation && this.presentation.manualRevealCorrectAnswers &&
        !this.presentation.audiencePacing && !this.slide.showCorrectOption
    },
    isShowPopupStreak() {
      return this.visiblePopupStreak &&
        this.result.streak >= 3 &&
        this.result.point > 0 &&
        !this.teamPlay
    },
    isActiveStreakBonus() {
      return this.presentation
        && this.presentation.isEnableStreakBonus
        && !this.presentation.teamPlay
    },
    isStreakBonusAdded() {
      return this.isActiveStreakBonus
        && this.result.streak >= 2
        && this.point >= this.result.point - this.result.streakBonusPoint
    },
    bonusTier() {
      if (!this.result || (this.result && this.result.streak < 2)) return 0

      if (this.result.streak < 6) return this.result.streak - 1

      return MAX_BONUS_TIER
    },
  },
  watch: {
    '$store.state.slide.quizStatus': function watchQuizStatus() {
      this.setPoint(this.result)
    },
    '$store.state.slide.showCorrectOption': function watchShowCorrectOption(isShowCorrectOption) {
      if (isShowCorrectOption) {
        this.setPoint(this.result)
      }
    },
    result: function watchResult(result) {
      this.setPoint(result)
    },
    'result.streak': {
      handler(value) {
        if (value && value % 3 === 0) {
          this.visiblePopupStreak = true
        }
      },
      immediate: true,
    },
  },

  mounted() {
    this.setPoint(this.result)

    document.body.classList.add('quiz-result-open')
  },
  destroyed() {
    if (this.updatePointInterval) {
      clearInterval(this.updatePointInterval)
    }

    document.body.classList.remove('quiz-result-open')
  },

  methods: {
    setPoint(result) {
      if (!result || !result.id) return
      if (this.isManualRevealAndNotShowCorrectOptionYet) return

      if (result.point === 0) {
        this.point = result.point
        return
      }
      if (!result.streakBonusPoint) {
        this.animatePointIncreasing(result.point)
      } else {
        this.animatePointIncreasingWithBonus(result.point, result.streakBonusPoint)
      }
    },

    animatePointIncreasing(totalPoint) {
      setTimeout(() => {
        this.point = 0
        let elapsedTime = 0
        const lastTime = Date.now()
        const increment = Math.ceil((totalPoint / ANIMATE_DURATION) * FPS)

        this.updatePointInterval = setInterval(() => {
          elapsedTime = Date.now() - lastTime
          if (elapsedTime < ANIMATE_DURATION) {
            if (this.point + increment > totalPoint) {
              this.point = totalPoint
              clearInterval(this.updatePointInterval)
            } else {
              this.point += increment
            }
          } else {
            this.point = totalPoint
            clearInterval(this.updatePointInterval)
          }
        }, FPS)
      }, 500)
    },

    finishAnimatingPoint(point, interval) {
      this.point = point
      clearInterval(interval)
    },

    showBonusBadgeAndAddBonusPoint(bonusPoint, totalPoint) {
      this.isShowBonusBadge = true
      // Hide the badge after 1 second
      setTimeout(() => {
        this.isShowBonusBadge = false

        // increase the bonus point after 1 second
        setTimeout(() => {
          let elapsedTime = 0
          const lastTime = Date.now()
          const increment = Math.ceil((bonusPoint / ANIMATE_DURATION) * FPS)
          this.updatePoint2Interval = setInterval(() => {
            elapsedTime = Date.now() - lastTime
            if (elapsedTime < ANIMATE_DURATION) {
              if (this.point + increment > totalPoint) {
                this.finishAnimatingPoint(totalPoint, this.updatePoint2Interval)
                this.showFinalAnimationEffect = true
              } else {
                this.point += increment
              }
            } else {
              this.finishAnimatingPoint(totalPoint, this.updatePoint2Interval)
              this.showFinalAnimationEffect = true
            }
          }, FPS)
        }, 700)
      }, 1000)
    },

    animatePointIncreasingWithBonus(totalPoint, bonusPoint) {
      const pointWithoutBonus = totalPoint - bonusPoint

      setTimeout(() => {
        this.point = 0
        let elapsedTime = 0
        const lastTime = Date.now()
        const increment = Math.ceil((pointWithoutBonus / ANIMATE_DURATION) * FPS)

        this.updatePointInterval = setInterval(() => {
          elapsedTime = Date.now() - lastTime
          if (elapsedTime < ANIMATE_DURATION) {
            if (this.point + increment > pointWithoutBonus) {
              this.finishAnimatingPoint(pointWithoutBonus, this.updatePointInterval)
              this.showBonusBadgeAndAddBonusPoint(bonusPoint, totalPoint)
            } else {
              this.point += increment
            }
          } else {
            this.finishAnimatingPoint(pointWithoutBonus, this.updatePointInterval)
            this.showBonusBadgeAndAddBonusPoint(bonusPoint, totalPoint)
          }
        }, FPS)
      }, 500)
    },
    handlePopupStreak(status = true) {
      this.visiblePopupStreak = status
    },
  },
}
</script>

<style lang="scss" scoped>
.quiz-result-container {
  padding: 16px 18px;

  &.invisible {
    visibility: hidden;
  }

  &.times-up {
    background-color: #F4AA47;

    .quiz-result-info {
      background-color: rgba(#B90909, 0.3);
      padding-left: 0;
      padding-right: 0;
    }

    .quiz-result-marker {
      left: -5px;
      top: -14px;
    }
  }

  &.correct {
    background-color: #33C173;

    .quiz-result-point {
      background-color: #20A15B;
    }
  }

  &.incorrect {
    background-color: #E04057;

    .quiz-result-point {
      background-color: #DA323C;
    }
  }

  &.is-audience-paced {
    padding-bottom: 76px;
  }
}

.quiz-result-info {
  position: relative;
  display: flex;
  padding: 9px 10px 9px 70px;
  justify-content: center;
  align-items: center;
  border-radius: 26px;
  background-color: rgba(#0A0A0A, 0.4);
  color: #fff;
  max-width: 423px;
  margin-left: auto;
  margin-right: auto;
}

.quiz-result-marker {
  flex-shrink: 0;
  position: absolute;
  left: -2px;
  top: -6px;

  .quiz-result-number {
    position: absolute;
    left: -5px;
    bottom: 3px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 3px solid #107F49;
    background-color: #fff;
    color: #107F49;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.quiz-result-text {
  flex-grow: 1;
  font-weight: 700;
  font-size: 20px;
}

.quiz-result-point {
  position: relative;
  border-radius: 20px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 66px;
  flex-shrink: 0;
  font-size: 14px;

  &.bonus > span {
    color: #FFE795;
  }
}

.streak-bonus {
  position: absolute;
  bottom: calc(100% + 12px);
  min-width: 66px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 32px;
  background: #FFF;
  box-shadow: 0px 0px 4px 0px rgba(51, 193, 115, 0.60);

  span {
    margin-top: 1px;
    line-height: 22px;
    font-weight: 700;
    color: #606060;
  }
}

.quiz-result-desc {
  margin-top: 8px;
  color: #fff;
  font-size: 14px;
  line-height: 24px;
  font-weight: 700;
}
</style>
